import { useCallback, useEffect, useRef, useState } from "react";
import { Brain, CheckCircle2, Loader2, MessageSquareText, Send, Sparkles, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

// The WhatsApp Brain — learns the team's real voice from exported chats,
// replies in English/Hindi/Urdu, and keeps learning from every staff reply.
export const BrainTab = () => {
  const [config, setConfig] = useState(null);
  const [profile, setProfile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [training, setTraining] = useState(false);
  const [testMsg, setTestMsg] = useState("");
  const [testReply, setTestReply] = useState("");
  const [testLang, setTestLang] = useState("");
  const [testing, setTesting] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/brain/config");
      setConfig(data);
      if (data.has_profile) {
        const { data: p } = await api.get("/admin/brain/profile");
        setProfile(p.profile);
      }
    } catch {
      /* quiet */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveMode = async (mode) => {
    try {
      const { data } = await api.put("/admin/brain/config", { mode });
      setConfig(data);
      toast.success(
        mode === "off" ? "Brain is off — built-in bot answers" :
        mode === "draft" ? "Draft mode — brain writes, staff approve before sending" :
        "Full auto — brain replies instantly in the client's language"
      );
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not save");
    }
  };

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const form = new FormData();
      for (const f of files) form.append("files", f);
      const { data } = await api.post("/admin/brain/upload", form, { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 });
      const ok = data.results.filter((r) => !r.error);
      const bad = data.results.filter((r) => r.error);
      if (ok.length) toast.success(`${data.total_messages.toLocaleString()} messages learned from ${ok.length} chat${ok.length === 1 ? "" : "s"}`);
      bad.forEach((r) => toast.error(`${r.file}: ${r.error}`));
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const toggleOwner = async (name) => {
    const owners = config.owner_senders.includes(name)
      ? config.owner_senders.filter((x) => x !== name)
      : [...config.owner_senders, name];
    try {
      const { data } = await api.put("/admin/brain/config", { owner_senders: owners });
      setConfig(data);
    } catch {
      toast.error("Could not save");
    }
  };

  const train = async () => {
    setTraining(true);
    try {
      const { data } = await api.post("/admin/brain/train", {}, { timeout: 240000 });
      setProfile(data.profile);
      await load();
      toast.success(`Brain trained on ${data.profile.owner_message_count.toLocaleString()} of your team's real messages`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Training failed — try again");
    } finally {
      setTraining(false);
    }
  };

  const test = async () => {
    if (!testMsg.trim()) return;
    setTesting(true);
    setTestReply("");
    try {
      const { data } = await api.post("/admin/brain/test", { message: testMsg.trim() }, { timeout: 120000 });
      setTestReply(data.reply);
      setTestLang(data.language);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  const modes = [
    ["off", "Off", "Built-in bot answers (rules + basic AI)"],
    ["draft", "Draft & approve", "Brain writes replies in your voice — staff tap Send"],
    ["auto", "Full auto-send", "Brain replies instantly, no review"],
  ];

  return (
    <div className="space-y-6" data-testid="brain-tab">
      {/* header + mode */}
      <div className="glass rounded-2xl p-6">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#B38B2D]">
            <Brain className="h-5 w-5 text-white" aria-hidden="true" />
          </span>
          <div>
            <h2 className="font-serif text-xl font-semibold text-[#1A1615]">The WhatsApp Brain</h2>
            <p className="text-xs font-light text-[#91837C]">
              Learns how your doctor and team actually write — then replies in English, Hindi or Urdu in that exact voice. Keeps learning from every reply your staff sends.
            </p>
          </div>
        </div>
        <div className="mt-4 grid gap-2 sm:grid-cols-3">
          {modes.map(([id, name, desc]) => (
            <button
              key={id}
              onClick={() => saveMode(id)}
              data-testid={`brain-mode-${id}`}
              className={`rounded-xl border p-3.5 text-left transition-all duration-200 ${
                config?.mode === id ? "border-[#D4AF37] bg-[#F8F3E6] shadow-[0_6px_18px_rgba(212,175,55,0.12)]" : "border-[#EBE3DA] bg-white hover:border-[#D4AF37]/50"
              }`}
            >
              <span className={`block text-xs font-semibold ${config?.mode === id ? "text-[#1A1615]" : "text-[#5A504B]"}`}>{name}</span>
              <span className="mt-0.5 block text-[10px] font-light leading-relaxed text-[#91837C]">{desc}</span>
            </button>
          ))}
        </div>
        {config && (
          <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-xs text-[#5A504B]" data-testid="brain-stats">
            <span><b className="text-[#1A1615]">{config.chat_count}</b> chats uploaded</span>
            <span><b className="text-[#1A1615]">{config.message_count.toLocaleString()}</b> messages in memory</span>
            <span><b className="text-[#1A1615]">{config.live_samples}</b> live staff replies learned</span>
            <span className={config.has_profile ? "inline-flex items-center gap-1 font-medium text-emerald-700" : "text-amber-600"}>
              {config.has_profile ? <><CheckCircle2 className="h-3.5 w-3.5" /> Trained {config.trained_at ? new Date(config.trained_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : ""}</> : "Not trained yet"}
            </span>
          </div>
        )}
      </div>

      {/* step 1 — upload */}
      <div className="glass rounded-2xl p-6" data-testid="brain-upload">
        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#B38B2D]">Step 1 · Feed it your real chats</p>
        <p className="mt-1 text-xs font-light leading-relaxed text-[#91837C]">
          In WhatsApp: open a client chat → ⋮ More → Export chat → Without media → you get a .txt file. Upload 3–5 chats (or your big combined export — thousands of messages welcome).
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept=".txt"
            multiple
            onChange={(e) => upload(Array.from(e.target.files || []))}
            className="hidden"
            data-testid="brain-upload-input"
          />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="brain-upload-button" className="btn-gold inline-flex items-center gap-2 px-5 py-2.5 text-[10px] disabled:opacity-60">
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload Chat Exports (.txt)
          </button>
          {config?.chat_count > 0 && (
            <button
              onClick={async () => {
                if (!window.confirm("Delete all uploaded training chats? (The learned style profile stays)")) return;
                await api.delete("/admin/brain/chats");
                toast.success("Uploaded chats cleared");
                load();
              }}
              data-testid="brain-clear-chats"
              className="btn-outline-lux inline-flex items-center gap-1.5 px-4 py-2.5 text-[10px]"
            >
              <Trash2 className="h-3 w-3" /> Clear uploads
            </button>
          )}
        </div>
      </div>

      {/* step 2 — who is you */}
      {config?.senders?.length > 0 && (
        <div className="glass rounded-2xl p-6" data-testid="brain-senders">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#B38B2D]">Step 2 · Which sender is you / your team?</p>
          <p className="mt-1 text-xs font-light text-[#91837C]">Tick every name that belongs to the clinic side (doctor, receptionist). The brain learns only from these voices.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {config.senders.map((s) => {
              const active = config.owner_senders.includes(s.name);
              return (
                <button
                  key={s.name}
                  onClick={() => toggleOwner(s.name)}
                  data-testid={`brain-sender-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={`rounded-full px-4 py-2 text-xs transition-colors ${active ? "bg-[#1A1615] font-semibold text-[#D4AF37]" : "bg-white text-[#5A504B] hover:text-[#1A1615]"}`}
                >
                  {s.name} <span className="ml-1 text-[9px] opacity-70">({s.count.toLocaleString()})</span>
                </button>
              );
            })}
          </div>
          <div className="mt-4">
            <button onClick={train} disabled={training || !config.owner_senders.length} data-testid="brain-train-button" className="btn-gold inline-flex items-center gap-2 px-6 py-3 text-[10px] disabled:opacity-60">
              {training ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              {config.has_profile ? "Retrain Brain" : "Train the Brain"}
            </button>
            {training && <p className="mt-2 text-[10px] font-light text-[#91837C]">Reading your team's messages… takes about a minute on the free engine.</p>}
          </div>
        </div>
      )}

      {/* profile + playground */}
      {profile && (
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="glass rounded-2xl p-6" data-testid="brain-profile">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#B38B2D]">Learned voice</p>
            <dl className="mt-3 space-y-2.5 text-xs">
              {[["Tone", profile.tone], ["Greetings", profile.greeting_style], ["Sign-offs", profile.signoff_style], ["Emoji use", profile.emoji_style], ["Typical length", profile.reply_length], ["Signature phrases", profile.common_phrases]].map(([k, v]) =>
                v ? (
                  <div key={k}>
                    <dt className="font-semibold text-[#1A1615]">{k}</dt>
                    <dd className="font-light leading-relaxed text-[#5A504B]">{v}</dd>
                  </div>
                ) : null
              )}
            </dl>
            {(profile.intent_templates || []).length > 0 && (
              <div className="mt-4 space-y-2">
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#B38B2D]">How they reply when…</p>
                {profile.intent_templates.slice(0, 5).map((t, i) => (
                  <div key={i} className="rounded-lg bg-white/70 p-3 text-xs">
                    <p className="font-medium text-[#1A1615]">{t.when}</p>
                    <p className="mt-0.5 font-light italic text-[#5A504B]">“{t.example}”</p>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="glass rounded-2xl p-6" data-testid="brain-playground">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#B38B2D]">Test-drive the brain</p>
            <p className="mt-1 text-xs font-light text-[#91837C]">Write like a client — in English, Hindi (हिंदी), Urdu (اردو) or roman ("kitna price hai"). The brain mirrors their language.</p>
            <textarea
              rows={3}
              className="input-lux mt-3 w-full resize-none text-sm"
              placeholder="e.g. Salam, hydrafacial ka price kya hai?"
              value={testMsg}
              onChange={(e) => setTestMsg(e.target.value)}
              data-testid="brain-test-input"
            />
            <button onClick={test} disabled={testing || !testMsg.trim()} data-testid="brain-test-send" className="btn-gold mt-2 inline-flex items-center gap-2 px-5 py-2.5 text-[10px] disabled:opacity-60">
              {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MessageSquareText className="h-3.5 w-3.5" />} Get Brain Reply
            </button>
            {testReply && (
              <div className="mt-4 rounded-xl border border-[#D4AF37]/30 bg-[#F8F3E6] p-4" data-testid="brain-test-reply">
                <p className="text-[9px] font-semibold uppercase tracking-[0.15em] text-[#8a6d1f]">Brain replies ({testLang})</p>
                <p className="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed text-[#1A1615]">{testReply}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
